import React from 'react';
import { Box, Divider, IconButton, MenuItem, Select, Slider, Typography } from '@material-ui/core';
import { FormatBold, FormatItalic, FormatUnderlined } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import { CirclePicker } from 'react-color';

const WEIGHT_TO_FONT_SIZE = { 1: 3.5, 2: 4.0, 3: 4.5, 4: 6.0, 5: 7.5 };

const FONT_FAMILIES = [
    // System / Web-safe
    { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { label: 'Times New Roman', value: '\'Times New Roman\', Times, serif' },
    { label: 'Courier New', value: '\'Courier New\', Courier, monospace' },
    { label: 'Georgia', value: 'Georgia, serif' },
    { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
    // Sans-serif (Google Fonts)
    { label: 'Roboto', value: '\'Roboto\', sans-serif' },
    { label: 'Open Sans', value: '\'Open Sans\', sans-serif' },
    { label: 'Lato', value: '\'Lato\', sans-serif' },
    { label: 'Montserrat', value: '\'Montserrat\', sans-serif' },
    { label: 'Poppins', value: '\'Poppins\', sans-serif' },
    { label: 'Nunito', value: '\'Nunito\', sans-serif' },
    { label: 'Raleway', value: '\'Raleway\', sans-serif' },
    { label: 'Inter', value: '\'Inter\', sans-serif' },
    { label: 'Oswald', value: '\'Oswald\', sans-serif' },
    { label: 'Ubuntu', value: '\'Ubuntu\', sans-serif' },
    // Serif (Google Fonts)
    { label: 'Merriweather', value: '\'Merriweather\', serif' },
    { label: 'Playfair Display', value: '\'Playfair Display\', serif' },
    { label: 'Lora', value: '\'Lora\', serif' },
    { label: 'PT Serif', value: '\'PT Serif\', serif' },
    { label: 'Noto Serif', value: '\'Noto Serif\', serif' },
    // Monospace (Google Fonts)
    { label: 'Source Code Pro', value: '\'Source Code Pro\', monospace' },
    { label: 'Fira Code', value: '\'Fira Code\', monospace' },
    { label: 'JetBrains Mono', value: '\'JetBrains Mono\', monospace' },
    // Handwriting / Display (Google Fonts)
    { label: 'Caveat', value: '\'Caveat\', cursive' },
    { label: 'Patrick Hand', value: '\'Patrick Hand\', cursive' },
    { label: 'Indie Flower', value: '\'Indie Flower\', cursive' },
    { label: 'Shadows Into Light', value: '\'Shadows Into Light\', cursive' },
    { label: 'Dancing Script', value: '\'Dancing Script\', cursive' },
];

const useStyles = makeStyles(() => ({
    root: {
        padding: '2px 16px',
        width: 230,
    },
    section: {
        paddingTop: '5px',
        paddingBottom: '5px',
    },
    label: {
        fontSize: '0.9rem',
        fontWeight: 600,
    },
    styleRow: {
        display: 'flex',
        gap: '4px',
        marginTop: '4px',
    },
    fontSelect: {
        width: '100%',
        marginTop: '4px',
        fontSize: '0.8rem',
    },
}));

const TextFormatPanel = ({
                             fontSize,
                             fontWeight,
                             fontFamily = 'Arial, Helvetica, sans-serif',
                             fontStyle = 'normal',
                             bold = 'normal',
                             textDecoration = 'none',
                             color = '#000000',
                             onFontSizeChange,
                             onFontSizeCommit,
                             onFontFamilyChange,
                             onFontStyleChange,
                             onBoldChange,
                             onTextDecorationChange,
                             onColorChange,
                             showColor = true,
                         }) => {
    const classes = useStyles();

    const [localSize, setLocalSize] = React.useState(null);
    const sizeValue = localSize != null ? localSize : (fontSize != null ? fontSize : (WEIGHT_TO_FONT_SIZE[fontWeight] || 3.5));

    return (
        <div
            className={classes.root}
            onPointerDown={(e) => {
                e.stopPropagation();
            }}
            onTouchStart={(e) => {
                e.stopPropagation();
            }}
        >
            {/* Font size slider */}
            <div className={classes.section}>
                <Typography className={classes.label}><FormattedMessage id='tooltip.lessonPage.fontSize' /></Typography>
                <Slider
                    value={sizeValue}
                    min={1.5}
                    max={12}
                    step={0.5}
                    valueLabelDisplay='off'
                    onChange={(e, val) => {
                        setLocalSize(val);
                        if (onFontSizeChange) onFontSizeChange(val);
                    }}
                    onChangeCommitted={(e, val) => {
                        if (onFontSizeCommit) onFontSizeCommit(val);
                    }}
                />
            </div>

            <Divider />

            {/* Font family dropdown */}
            <div className={classes.section}>
                <Typography className={classes.label}><FormattedMessage id='tooltip.lessonPage.font' /></Typography>
                <Select
                    value={fontFamily}
                    onChange={(e) => {
                        if (onFontFamilyChange) onFontFamilyChange(e.target.value);
                    }}
                    className={classes.fontSelect}
                    variant='outlined'
                    margin='dense'
                    MenuProps={{ disablePortal: true, PaperProps: { style: { maxHeight: 250 } } }}
                >
                    {FONT_FAMILIES.map((f) => (
                        <MenuItem key={f.label} value={f.value}>
                            <span style={{ fontFamily: f.value }}>{f.label}</span>
                        </MenuItem>
                    ))}
                </Select>
            </div>

            <Divider />

            {/* Bold / Italic / Underline toggles */}
            <div className={classes.section}>
                <Typography className={classes.label}>Style</Typography>
                <div className={classes.styleRow}>
                    <IconButton
                        size='small'
                        onClick={() => {
                            if (onBoldChange) onBoldChange(bold === 'bold' ? 'normal' : 'bold');
                        }}
                        color={bold === 'bold' ? 'primary' : 'default'}
                    >
                        <FormatBold fontSize='small' />
                    </IconButton>
                    <IconButton
                        size='small'
                        onClick={() => {
                            if (onFontStyleChange) onFontStyleChange(fontStyle === 'italic' ? 'normal' : 'italic');
                        }}
                        color={fontStyle === 'italic' ? 'primary' : 'default'}
                    >
                        <FormatItalic fontSize='small' />
                    </IconButton>
                    <IconButton
                        size='small'
                        onClick={() => {
                            if (onTextDecorationChange) onTextDecorationChange(textDecoration === 'underline' ? 'none' : 'underline');
                        }}
                        color={textDecoration === 'underline' ? 'primary' : 'default'}
                    >
                        <FormatUnderlined fontSize='small' />
                    </IconButton>
                </div>
            </div>

            {/* Color picker */}
            {showColor && onColorChange && (
                <>
                    <Divider />
                    <div className={classes.section}>
                        <Typography className={classes.label}><FormattedMessage
                            id='tooltip.lessonPage.color' /></Typography>
                        <Box style={{ paddingTop: '8px' }}>
                            <CirclePicker
                                disableAlpha
                                color={color}
                                colors={[
                                    '#000000', '#f44336', '#e91e63', '#9c27b0',
                                    '#673ab7', '#3f51b5', '#2196f3', '#03a9f4',
                                    '#009688', '#4caf50', '#8bc34a', '#cddc39',
                                    '#ffeb3b', '#ffc107', '#ff9800', '#ff5722',
                                    '#795548', '#607d8b',
                                ]}
                                onChangeComplete={(c) => {
                                    onColorChange(c.hex);
                                }}
                            />
                        </Box>
                    </div>
                </>
            )}
        </div>
    );
};

export default TextFormatPanel;
